import { useJobSummaries, useUsers } from "@vidario/api-client";
import {
  CANDIDATE_NOUN,
  countLabel,
  DASHBOARD_PAGE_SIZE,
  JOB_NOUN,
  dashboardJobs,
  pageRangeLabel,
  type JobSummary,
  type User,
} from "@vidario/domain";
import {
  Button,
  EmptyState,
  FilterChip,
  Input,
  JOB_STATUS_MAP,
  Pagination,
  Select,
  type JobStatus,
} from "@vidario/ui";
import { Briefcase, Download, Plus, RefreshCw, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useRole } from "../../guards/useRole";
import { CloseJobDialog } from "../longlist/CloseJobDialog";
import { JobCard } from "./JobCard";
import { JobTable } from "./JobTable";
import { ViewToggle, type DashboardView } from "./ViewToggle";

const SEARCH_DEBOUNCE_MS = 300;

/** Stellenübersicht (MVP_requirements.md §4 Screen 1). Landing page for
 * the signed-in user. Cards/Table toggle, search + status + (admin only)
 * recruiter filter, Export placeholder, + Neue Stelle. Recruiter rows
 * are filtered server-side (mock emulates Supabase RLS) so admins see
 * every Stelle and recruiters see only their own. */
export function DashboardRoute(): ReactElement {
  const navigate = useNavigate();
  const role = useRole();
  const isAdmin = role === "admin";

  // Page, filters and view live in the URL, so reload, back and a shared
  // link keep the place (#13). Any change but the page starts at page 1.
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  // A mistyped or outdated link must not pick a status that doesn't exist.
  const rawStatus = params.get("status");
  const statusFilter: "all" | JobStatus =
    rawStatus && rawStatus in JOB_STATUS_MAP ? (rawStatus as JobStatus) : "all";
  const creatorFilter = params.get("recruiter") ?? "all";
  const view: DashboardView = params.get("view") === "table" ? "table" : "cards";
  const requestedPage = Number(params.get("page")) || 1;

  const updateParams = (
    patch: Record<string, string | null>,
    options?: { replace?: boolean },
  ): void => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!("page" in patch) && !("view" in patch)) next.delete("page");
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || v === "all") next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      options?.replace ? { replace: true } : undefined,
    );
  };
  const setStatusFilter = (v: "all" | JobStatus): void => updateParams({ status: v });
  const setCreatorFilter = (v: string): void => updateParams({ recruiter: v });
  const setView = (v: DashboardView): void => updateParams({ view: v === "cards" ? null : v });

  // The search field answers at once; the URL follows after a pause.
  const [searchInput, setSearchInput] = useState(search);
  // Back/forward or a reset changes the URL's search: show it in the field too.
  const [syncedSearch, setSyncedSearch] = useState(search);
  if (search !== syncedSearch) {
    setSyncedSearch(search);
    setSearchInput(search);
  }
  useEffect(() => {
    if (searchInput === search) return;
    const t = setTimeout(
      () => updateParams({ search: searchInput }, { replace: true }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateParams is recreated each render
  }, [searchInput, search]);
  const setSearch = (v: string): void => {
    setSearchInput(v);
    updateParams({ search: v }, { replace: true });
  };

  const { data: jobs, isLoading, error } = useJobSummaries();
  const { data: users } = useUsers();
  const [closing, setClosing] = useState<JobSummary | null>(null);

  const usersById = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of users ?? []) map.set(u.id, u);
    return map;
  }, [users]);

  const recruiterOptions = useMemo(() => {
    const recruiters = (users ?? []).filter((u) => u.role === "recruiter");
    return [
      { value: "all", label: "Alle Recruiter" },
      ...recruiters.map((u) => ({ value: String(u.id), label: u.name })),
    ];
  }, [users]);

  const result = useMemo(
    () =>
      dashboardJobs(
        jobs ?? [],
        { search, status: statusFilter, recruiter: creatorFilter },
        requestedPage,
      ),
    [jobs, search, statusFilter, creatorFilter, requestedPage],
  );
  const shown = result.jobs;
  const hasActiveFilters = statusFilter !== "all" || creatorFilter !== "all" || Boolean(search);

  // A filter that shrank the list can leave the URL past the last page.
  useEffect(() => {
    if (jobs && result.page !== requestedPage) {
      updateParams({ page: result.page === 1 ? null : String(result.page) }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateParams is recreated each render
  }, [jobs, result.page, requestedPage]);

  const listRef = useRef<HTMLDivElement>(null);
  const changePage = (next: number): void => {
    updateParams({ page: next === 1 ? null : String(next) });
    listRef.current?.scrollIntoView({ block: "start" });
  };

  const resetFilters = (): void => {
    setSearchInput("");
    updateParams({ status: null, recruiter: null, search: null });
  };

  /** Drafts resume into the editor; everything else opens the longlist. */
  const openJob = (job: { id: string; status: JobStatus }): void => {
    const target = job.status === "draft" ? `/jobs/${job.id}/edit` : `/jobs/${job.id}/longlist`;
    void navigate(target);
  };

  return (
    <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8 pb-20">
      <header className="mb-8 flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {isAdmin ? "Admin-Ansicht" : "Recruiter-Ansicht"}
          </div>
          <h1 className="m-0 text-3xl font-bold -tracking-[0.5px]">
            {isAdmin ? "Alle Stellen" : "Stellenübersicht"}
          </h1>
          {jobs && (
            <p className="mt-1.5 text-sm text-text-tertiary">
              {result.count === 0
                ? "Keine Stellen"
                : `${countLabel(result.count, JOB_NOUN)} · ${countLabel(result.candidateCount, CANDIDATE_NOUN, { zero: "noch keine Kandidaten" })}`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            leftIcon={<Download size={16} />}
            disabled
            title="Export kommt in einer späteren Phase"
          >
            Export
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={() => void navigate("/jobs/new")}>
            Neue Stelle
          </Button>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="max-w-sm flex-1 basis-72">
          <Input
            icon={<Search size={16} />}
            placeholder="Stelle oder Firma suchen…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Suche"
          />
        </div>
        <div className="min-w-44">
          <Select
            aria-label="Status-Filter"
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as "all" | JobStatus)}
            options={[
              { value: "all", label: "Alle Status" },
              ...Object.entries(JOB_STATUS_MAP).map(([value, cfg]) => ({
                value,
                label: cfg.label,
              })),
            ]}
          />
        </div>
        {isAdmin && (
          <div className="min-w-48" data-testid="recruiter-filter">
            <Select
              aria-label="Recruiter-Filter"
              value={creatorFilter}
              onValueChange={(v) => setCreatorFilter(v)}
              options={recruiterOptions}
            />
          </div>
        )}
        <div className="flex-1" />
        <ViewToggle value={view} onChange={setView} />
      </div>

      {hasActiveFilters && (
        <div className="mb-5 flex flex-wrap items-center gap-2 text-[13px] text-text-tertiary">
          <span>Aktive Filter:</span>
          {statusFilter !== "all" && (
            <FilterChip onRemove={() => setStatusFilter("all")}>
              Status: {JOB_STATUS_MAP[statusFilter].label}
            </FilterChip>
          )}
          {creatorFilter !== "all" && (
            <FilterChip onRemove={() => setCreatorFilter("all")}>
              Recruiter: {users?.find((u) => String(u.id) === creatorFilter)?.name ?? creatorFilter}
            </FilterChip>
          )}
          {search && <FilterChip onRemove={() => setSearch("")}>„{search}“</FilterChip>}
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex text-[13px] font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Alle zurücksetzen
          </button>
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-text-secondary" role="status">
          Lade Stellen…
        </p>
      )}
      {error && (
        <p className="text-sm text-danger-600" role="alert">
          Fehler beim Laden: {error.message}
        </p>
      )}

      {jobs && result.count === 0 && hasActiveFilters && (
        <EmptyState
          icon={<Briefcase size={28} />}
          title="Keine Stellen gefunden"
          description="Keine Stelle entspricht den aktuellen Filtern."
          action={
            <Button variant="secondary" leftIcon={<RefreshCw size={16} />} onClick={resetFilters}>
              Filter zurücksetzen
            </Button>
          }
        />
      )}

      {jobs && result.count === 0 && !hasActiveFilters && (
        <EmptyState
          icon={<Briefcase size={28} />}
          title="Noch keine Stellen"
          description="Lege deine erste Stelle an, um Kandidaten zu empfangen."
          action={
            <Button leftIcon={<Plus size={16} />} onClick={() => void navigate("/jobs/new")}>
              Neue Stelle
            </Button>
          }
        />
      )}

      <div ref={listRef} className="scroll-mt-6" />
      {shown.length > 0 && view === "cards" && (
        <ul
          aria-label="Stellenliste"
          className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(min(21rem,100%),1fr))]"
          data-testid="dashboard-jobs-list"
        >
          {shown.map((j) => (
            <li key={j.id}>
              <JobCard
                job={j}
                isAdmin={isAdmin}
                creator={usersById.get(j.createdBy)}
                onOpen={() => openJob(j)}
                onCloseJob={() => setClosing(j)}
              />
            </li>
          ))}
        </ul>
      )}

      {shown.length > 0 && view === "table" && (
        <JobTable
          jobs={shown}
          isAdmin={isAdmin}
          creators={usersById}
          onOpen={(jobId) => {
            const job = shown.find((j) => j.id === jobId);
            if (job) openJob(job);
          }}
        />
      )}

      {result.count > 0 && (
        <Pagination
          className="mt-6"
          page={result.page}
          pageCount={result.pageCount}
          onPageChange={changePage}
          summary={pageRangeLabel(result.page, DASHBOARD_PAGE_SIZE, result.count, JOB_NOUN)}
        />
      )}
      <p className="sr-only" role="status">
        {result.pageCount > 1 ? `Seite ${result.page} von ${result.pageCount}` : ""}
      </p>
      {closing && (
        <CloseJobDialog
          job={closing}
          open
          onOpenChange={(o) => {
            if (!o) setClosing(null);
          }}
        />
      )}
    </main>
  );
}
